import { ExclamationCircleIcon } from "@heroicons/react/20/solid";
import { FC, Fragment } from "react";
import { t } from "i18next";
import { Label, Listbox } from "@headlessui/react";
import { ButtonSelect } from "../molecules/ButtonSelect";
import { OptionsSelect } from "../molecules/OptionsSelect";

type option = { id: string | null | number; name: string };
export type SelectProps = {
  selected: option | undefined;

  /**
   * the function to set the selected option
   * @example setSelected({ id: 1, name: "Option 1" })
   */
  setSelected: (value: option) => void;

  /**
   * the options of the select
   * @example [{ id: 1, name: "Option 1" }, { id: 2, name: "Option 2" }]
   */
  options: option[];

  tittle: string;

  textDescribe?: string;
  /**
   * the name of the select
   */
  name?: string;
  /**
   * the error of the select
   */
  error?: boolean;
  /**
   * error text of the select
   */
  textError?: string;
  /**
   * the label of the select
   */
  span?: string;

  disabled?: boolean;

  reverse?: boolean;

  addDefault?: boolean;
};

export const Select: FC<SelectProps> = ({
  selected,
  setSelected,
  options,
  tittle,
  textDescribe,
  error,
  textError,
  name,
  span,
  disabled = false,
  reverse = false,
  addDefault = true,
}) => {
  const defaultOption: option = {
    id: undefined,
    name: t("general.select_option"),
  };
  const allOptions = addDefault
    ? [defaultOption, ...(options ?? [])]
    : (options ?? []);

  return (
    <Fragment>
      <Listbox value={selected} onChange={setSelected} name={name}>
        <div className="w-full">
          <div className="flex justify-between">
            <Label className="block text-sm font-medium leading-6 text-gray-600">
              {tittle}
            </Label>
            <span className={`text-sm leading-6 text-gray-500`}>{span}</span>
          </div>

          <div className="relative">
            <ButtonSelect
              error={textError}
              nameSelected={selected?.name || "Seleccione una opción"}
              disabled={disabled}
            />
            <OptionsSelect reverse={reverse} options={allOptions} />

            {error ? (
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <ExclamationCircleIcon
                  aria-hidden="true"
                  className="h-5 w-5 text-red-500"
                />
              </div>
            ) : (
              <></>
            )}
          </div>

          {error ? (
            <>
              <p className={`mt-1 text-sm text-red-600`}>{textError}</p>
            </>
          ) : (
            <></>
          )}

          {/* Descripción en gris */}
          <p className="mt-1 text-sm text-gray-400">{textDescribe}</p>
          {error && <p className={`mt-1 text-sm text-red-600`}>{error}</p>}
        </div>
      </Listbox>
    </Fragment>
  );
};

type SelectRuleMaxProps = {
  value: number;
  options: option[];
  onChange: (val: number) => void;
};

export const SelectRuleMax: FC<SelectRuleMaxProps> = ({
  value,
  onChange,
  options,
}) => {
  // Convert value to number for consistent comparison, handle edge cases
  const numericValue = value === "" || value == null ? 0 : Number(value);

  // Find the selected option based on the numeric value
  const selectedOption = options.find((opt) => {
    // Handle "Ilimitado" case
    if (opt.name === "Ilimitado") {
      return numericValue === 100000;
    }
    // Handle numeric options - also check for string representation
    const optionValue = Number(opt.name);
    return !isNaN(optionValue) && optionValue === numericValue;
  });

  return (
    <Select
      selected={selectedOption}
      setSelected={(option) => {
        // Handle case where no option is selected (default option)
        if (!option?.name || option.id === undefined) {
          return; // Don't call onChange for default option
        }

        if (option.name === "Ilimitado") {
          onChange(100000);
        } else {
          const numericValue = Number(option.name);
          if (!isNaN(numericValue)) {
            onChange(numericValue);
          }
        }
      }}
      options={options}
      tittle={t("branch.views.rule_2")}
      textDescribe={t("branch.views.rule_2_desc")}
    />
  );
};
